import React from 'react'
import { useTouchFeedback } from '../../hooks/useTouchFeedback'
import { getTouchTargetClasses } from '../../utils/touchTargets'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'outline' | 'secondary' | 'success' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  loadingText?: string
  children: React.ReactNode
  /**
   * Enable ripple effect on touch (default: false)
   */
  enableRipple?: boolean
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'default',
  size = 'md',
  loading = false,
  loadingText = 'Processing...',
  children,
  className = '',
  disabled,
  enableRipple = false,
  'aria-label': ariaLabel,
  ...props
}) => {
  const { touchFeedbackClasses, touchHandlers, shouldDisableHover } = useTouchFeedback({
    enableScaling: true,
    enableRipple,
    disableHoverOnTouch: true
  })

  const baseClasses = 'rounded-lg font-medium transition-smooth focus-ring disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center'

  // Touch target size classes - ensures minimum sizes across breakpoints
  const touchTargetClasses = getTouchTargetClasses()

  const sizeClasses = {
    // Mobile: 44px min, Tablet: 44px min, Desktop: 36px min
    sm: 'px-3 py-1.5 text-sm min-h-[44px] md:min-h-[40px] lg:min-h-[36px]',
    // Mobile: 48px min, Tablet: 44px min, Desktop: 40px min
    md: 'px-4 py-2.5 min-h-[48px] md:min-h-[44px] lg:min-h-[40px]',
    // Mobile: 56px min, Tablet: 52px min, Desktop: 48px min
    lg: 'px-6 py-3 text-lg min-h-[56px] md:min-h-[52px] lg:min-h-[48px]'
  }

  const variantClasses = {
    default: 'bg-admin-orange-500 hover:bg-admin-orange-600 text-white shadow-sm focus:ring-admin-orange-500',
    primary: 'bg-admin-orange-500 hover:bg-admin-orange-600 text-white shadow-sm focus:ring-admin-orange-500',
    outline: 'border border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-700 focus:ring-admin-orange-500',
    secondary: 'bg-gray-700 hover:bg-gray-800 text-white shadow-sm focus:ring-gray-600',
    success: 'bg-green-500 hover:bg-green-600 text-white shadow-sm focus:ring-green-500',
    danger: 'bg-red-500 hover:bg-red-600 text-white shadow-sm focus:ring-red-500'
  }

  // Conditionally disable hover classes on touch devices
  const hoverClasses = shouldDisableHover ? '' : variantClasses[variant]
  const baseVariantClasses = shouldDisableHover
    ? variantClasses[variant].replace(/hover:[^\s]+/g, '').trim()
    : variantClasses[variant]

  // Determine the aria-label based on loading state
  // When loading, append loading text to the label for screen readers
  const effectiveAriaLabel = loading && ariaLabel
    ? `${ariaLabel} - ${loadingText}`
    : ariaLabel

  return (
    <button
      className={`${baseClasses} ${touchFeedbackClasses} ${sizeClasses[size]} ${baseVariantClasses} ${className}`}
      disabled={disabled || loading}
      aria-disabled={disabled || loading}
      aria-busy={loading}
      aria-label={effectiveAriaLabel}
      {...touchHandlers}
      {...props}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>{loadingText}</span>
        </span>
      ) : (
        children
      )}
    </button>
  )
}
